/**
 * FileKey Configuration
 * 
 * Defines metadata for each file type used in the audit system.
 */

import type { FileKey } from './types';

export interface FileKeyConfig {
  key: FileKey;
  nameKey: string;           // i18n key for display name
  descriptionKey: string;    // i18n key for tooltip/description
  formats: string[];         // Accepted file formats
  exampleFileName?: string;  // Example filename for guidance
  expectedFields?: string[]; // Columns the file should contain
  exampleData?: string;      // Example row with typical data
  allowMultiple?: boolean;   // Allow uploading multiple files for this key
  multipleHint?: string;     // Hint text for multiple files (i18n key)
}

// ============= File Key Configurations =============

export const FILE_KEY_CONFIGS: Record<FileKey, FileKeyConfig> = {
  GL_SIE: {
    key: 'GL_SIE',
    nameKey: 'files.glSie.name',
    descriptionKey: 'files.glSie.description',
    formats: ['SIE', 'SE'],
    exampleFileName: 'huvudbok.se',
    // SIE format is standardized - no expectedFields needed
  },
  GL_SIE_PREV: {
    key: 'GL_SIE_PREV',
    nameKey: 'files.glSiePrev.name',
    descriptionKey: 'files.glSiePrev.description',
    formats: ['SIE', 'SE'],
    exampleFileName: 'huvudbok_foregaende.se',
    // SIE format is standardized - no expectedFields needed
  },
  COA: {
    key: 'COA',
    nameKey: 'files.coa.name',
    descriptionKey: 'files.coa.description',
    formats: ['CSV', 'XLSX', 'PDF'],
    exampleFileName: 'kontoplan.xlsx',
    expectedFields: ['Kontonummer', 'Kontonamn', 'Kontotyp'],
    exampleData: '1910;Kassa;Tillgång',
  },
  JOURNAL_META: {
    key: 'JOURNAL_META',
    nameKey: 'files.journalMeta.name',
    descriptionKey: 'files.journalMeta.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'verifikationslogg.csv',
    expectedFields: ['Verifikationsnr', 'Datum', 'Text', 'Användare', 'Tidpunkt'],
    exampleData: 'A-456;2024-05-20;Leverantörsfaktura;anna.svensson;2024-05-20 14:32',
  },
  AP_OPEN_ITEMS: {
    key: 'AP_OPEN_ITEMS',
    nameKey: 'files.apOpenItems.name',
    descriptionKey: 'files.apOpenItems.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'leverantorsreskontra.xlsx',
    expectedFields: ['Fakturanummer', 'Leverantör', 'Belopp', 'Förfallodatum'],
    exampleData: '12345;ABC Konsult AB;15000;2024-12-31',
  },
  AR_OPEN_ITEMS: {
    key: 'AR_OPEN_ITEMS',
    nameKey: 'files.arOpenItems.name',
    descriptionKey: 'files.arOpenItems.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'kundreskontra.xlsx',
    expectedFields: ['Fakturanummer', 'Kund', 'Belopp', 'Förfallodatum'],
    exampleData: '2024-001;Kund AB;25000;2024-12-15',
  },
  BANK_TX: {
    key: 'BANK_TX',
    nameKey: 'files.bankTx.name',
    descriptionKey: 'files.bankTx.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'banktransaktioner.csv',
    expectedFields: ['Datum', 'Text', 'Belopp', 'Saldo'],
    exampleData: '2024-06-15;Lön personal;-125000;543210',
    allowMultiple: true,
    multipleHint: 'files.bankTx.multipleHint',
  },
  TAX_ACCOUNT: {
    key: 'TAX_ACCOUNT',
    nameKey: 'files.taxAccount.name',
    descriptionKey: 'files.taxAccount.description',
    formats: ['CSV', 'XLSX', 'PDF'],
    exampleFileName: 'skattekonto.csv',
    expectedFields: ['Datum', 'Händelse', 'Belopp', 'Saldo'],
    exampleData: '2024-03-12;Arbetsgivaravgift;-45230;-12500',
    allowMultiple: true,
    multipleHint: 'files.taxAccount.multipleHint',
  },
  VAT_REPORT: {
    key: 'VAT_REPORT',
    nameKey: 'files.vatReport.name',
    descriptionKey: 'files.vatReport.description',
    formats: ['PDF', 'XLSX'],
    exampleFileName: 'momsrapport.pdf',
    expectedFields: ['Period', 'Utgående moms', 'Ingående moms', 'Moms att betala'],
    exampleData: '2024-01;52000;38000;14000',
    allowMultiple: true,
    multipleHint: 'files.vatReport.multipleHint',
  },
  VAT_TX_DETAIL: {
    key: 'VAT_TX_DETAIL',
    nameKey: 'files.vatTxDetail.name',
    descriptionKey: 'files.vatTxDetail.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'moms_transaktioner.csv',
    expectedFields: ['Verifikation', 'Datum', 'Konto', 'Momsbelopp', 'Momskod'],
    exampleData: 'A-123;2024-03-15;2640;5200;25%',
    allowMultiple: true,
    multipleHint: 'files.vatTxDetail.multipleHint',
  },
  VENDOR_MASTER: {
    key: 'VENDOR_MASTER',
    nameKey: 'files.vendorMaster.name',
    descriptionKey: 'files.vendorMaster.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'leverantorsregister.xlsx',
    expectedFields: ['Leverantörsnr', 'Namn', 'Org.nr', 'Betalvillkor'],
    exampleData: '1001;ABC Konsult AB;556123-4567;30 dagar',
  },
  CUSTOMER_MASTER: {
    key: 'CUSTOMER_MASTER',
    nameKey: 'files.customerMaster.name',
    descriptionKey: 'files.customerMaster.description',
    formats: ['CSV', 'XLSX'],
    exampleFileName: 'kundregister.xlsx',
    expectedFields: ['Kundnr', 'Namn', 'Org.nr', 'Kreditgräns'],
    exampleData: '2001;Kund AB;556789-0123;100000',
  },
  AGI_REPORTS: {
    key: 'AGI_REPORTS',
    nameKey: 'files.agiReports.name',
    descriptionKey: 'files.agiReports.description',
    formats: ['PDF', 'XLSX'],
    exampleFileName: 'arbetsgivardeklaration.pdf',
    expectedFields: ['Period', 'Bruttolön', 'Arbetsgivaravgift', 'Avdragen skatt'],
    exampleData: '2024-03;450000;141525;112500',
    allowMultiple: true,
    multipleHint: 'files.agiReports.multipleHint',
  },
};

// ============= Helper Functions =============

/**
 * Get all file keys
 */
export function getAllFileKeys(): FileKey[] {
  return Object.keys(FILE_KEY_CONFIGS) as FileKey[];
}

/**
 * Get file key config
 */
export function getFileKeyConfig(fileKey: FileKey): FileKeyConfig {
  return FILE_KEY_CONFIGS[fileKey];
}

/**
 * Check if a file extension matches allowed formats
 */
export function isValidFileFormat(fileKey: FileKey, fileName: string): boolean {
  const config = FILE_KEY_CONFIGS[fileKey];
  const extension = fileName.split('.').pop()?.toUpperCase() || '';
  return config.formats.some(f => f.toUpperCase() === extension);
}

/**
 * Get display name for file key (fallback if i18n not available)
 */
export function getFileKeyDisplayName(fileKey: FileKey): string {
  const displayNames: Record<FileKey, string> = {
    GL_SIE: 'Huvudbok (SIE)',
    GL_SIE_PREV: 'Huvudbok föregående år (SIE)',
    COA: 'Kontoplan',
    JOURNAL_META: 'Verifikationsmetadata',
    AP_OPEN_ITEMS: 'Leverantörsreskontra',
    AR_OPEN_ITEMS: 'Kundreskontra',
    BANK_TX: 'Banktransaktioner',
    TAX_ACCOUNT: 'Skattekonto',
    VAT_REPORT: 'Momsrapport',
    VAT_TX_DETAIL: 'Momstransaktioner',
    VENDOR_MASTER: 'Leverantörsregister',
    CUSTOMER_MASTER: 'Kundregister',
    AGI_REPORTS: 'Arbetsgivardeklarationer',
  };
  return displayNames[fileKey];
}
